import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';

export class BreadcrumbOptions {
	expandClassName?: string;
	itemClassName?: string;
	itemStaticClassName?: string;
	initializedClassName?: string;
	hiddenClassName?: string;
	scrollToLabel?: boolean;
	shrinkMode?: 'None' | 'Width';
	shrinkCap?: number;
	breakpoints?: {
		maxWidth: number;
		shrinkMode?: 'None' | 'Width';
	};
}

export class Breadcrumb {
	private options: BreadcrumbOptions = {
		expandClassName: 'js-breadcrumb-expand',
		itemClassName: 'js-breadcrumb-item',
		itemStaticClassName: 'js-breadcrumb-static-item',
		initializedClassName: 'is-initialized',
		hiddenClassName: 'is-hidden',
		scrollToLabel: false,
		shrinkMode: 'None',
		shrinkCap: 5,
		breakpoints: {
			maxWidth: undefined,
			shrinkMode: 'Width',
		},
	};

	// todo: config? or data-expanded="true|false"
	private expanded = true;
	private listElement: HTMLElement;
	private itemElements: HTMLElement[];

	constructor(private element: HTMLElement, options?: BreadcrumbOptions) {
		this.options = { ...this.options, ...options };

		this.listElement = this.element.firstElementChild as HTMLElement;

		this._init();
	}

	private _init() {
		this.itemElements = Array.from(
			this.element.getElementsByClassName(this.options.itemClassName)
		) as HTMLElement[];

		if (this.itemElements && this.itemElements.length) {
			const togglers = Array.from(
				document.getElementsByClassName(this.options.expandClassName)
			) as HTMLElement[];

			togglers.forEach((button) =>
				button.addEventListener('click', (event) =>
					this._handleClick(event, button)
				)
			);

			// todo: better way?
			// slice(1) to exlude expander
			if (this.options.scrollToLabel) {
				this.itemElements.slice(1).forEach((item, index) => {
					//TODO: js-breadcrumb-label?
					const link = item.getElementsByTagName('a')[0];

					if (link) {
						link.addEventListener('click', (event) => {
							// only prevent default if we have a menu
							// otherwise normal links doesn' work anymore

							if (link.href.indexOf('#menu-breadcrumb') !== -1) {
								event.preventDefault();
								this._setFocus(index);
							}
						});
					}
				});
			}

			// TODO: more shrink modes?
			// shrink the breadcrumb if children are bigger than the container
			if (this.options.shrinkMode === 'Width') {
				if (
					this.expanded &&
					this._getItemTotalWidth() > this.element.offsetWidth &&
					this.itemElements.length > 2
				) {
					this._shrink(togglers);
				}
			}

			// TODO: more shrink modes?
			// shrink the breadcrumb if we hit the breakpoint
			if (
				this.options.breakpoints &&
				this.options.breakpoints.shrinkMode === 'Width'
			) {
				if (
					this.expanded &&
					this.options.breakpoints.maxWidth &&
					window.innerWidth < this.options.breakpoints.maxWidth &&
					this.itemElements.length > 2
				) {
					this._shrink(togglers);
				}
			}
		}

		if (this.options.initializedClassName) {
			addClass(this.element, this.options.initializedClassName);
		}
	}

	/**
	 * Get the total width of all child elements
	 */
	private _getItemTotalWidth() {
		let totalWidth = 0;

		Array.from(
			document.querySelectorAll<HTMLElement>(
				`.${this.options.itemClassName}:not(.${this.options.hiddenClassName})`
			)
		).forEach((item) => (totalWidth += item.offsetWidth));

		if (this.options.itemStaticClassName) {
			Array.from(
				document.querySelectorAll<HTMLElement>(
					`.${this.options.itemStaticClassName}:not(.${this.options.hiddenClassName})`
				)
			).forEach((item) => {
				totalWidth += item.offsetWidth;
			});
		}

		return totalWidth;
	}

	/**
	 * Shrink the breadcrumb
	 */
	private _shrink(togglers: HTMLElement[]) {
		if (!this.expanded) {
			return;
		}

		this.expanded = false;
		togglers.forEach((toggler) => this._toggle(toggler));
	}

	/**
	 * Checks if the last item is still too big when the breadcrumb is shrinked.
	 * You need to add text-overflow: ellipsis and overflow: hidden for better UX.
	 */
	private _checkWidth() {
		if (this.options.shrinkMode !== 'Width') {
			return;
		}

		const items = Array.from(
			document.querySelectorAll<HTMLElement>(
				`.${this.options.itemClassName}:not(.${this.options.hiddenClassName})`
			)
		);

		if (!items.length) {
			return;
		}

		const lastItem = items[items.length - 1];

		// reset width
		lastItem.style.width = null;

		if (this.expanded) {
			return;
		}

		const totalWidth = this._getItemTotalWidth();
		const maxWidth = this.element.offsetWidth;
		const cap = this.options.shrinkCap || 0;

		//TODO: add cap here (totalWidth +/- cap)? Does it matter?
		if (totalWidth < maxWidth) {
			return;
		}

		if (items.length !== 2) {
			return;
		}

		// set new width
		const cut = totalWidth - maxWidth + cap;
		lastItem.style.width = `${lastItem.offsetWidth - cut}px`;
	}

	/**
	 * Sets focus on the HTML element for the label and scrolls it into the view if
	 * scrolling is enabled.
	 */
	private _setFocus(index: number) {
		this._scrollToLabel(index);

		if (this.itemElements && this.itemElements.length) {
			this.itemElements[index].focus();
		}
	}

	/**
	 * Moves the list such that the desired label (marked by index) is moved into view.
	 */
	private _scrollToLabel(index: number) {
		const selected = this.itemElements ? this.itemElements[index + 1] : null;

		if (!selected) {
			return;
		}

		this._updateScrollPosition(selected.offsetLeft);
	}

	/** Performs transformation on the list that will cause the list to scroll. */
	private _updateScrollPosition(scrollDistance: number) {
		this.listElement.scrollLeft = scrollDistance + 20;
	}

	private _handleClick(event: Event, button: HTMLElement) {
		event.preventDefault();

		this.expanded = !this.expanded;

		this._toggle(button);

		// scroll to last one...
		this._scrollToLabel(this.itemElements.length - 1);
	}

	private _toggle(button: HTMLElement) {
		if (this.expanded) {
			addClass(button.parentElement, this.options.hiddenClassName);
			this._updateAria(button.parentElement, false);

			// slice(1) to exlude expander and last item
			this.itemElements.slice(1).forEach((item) => {
				removeClass(item, this.options.hiddenClassName);
				this._updateAria(item, true);
			});
		} else {
			removeClass(button.parentElement, this.options.hiddenClassName);
			this._updateAria(button.parentElement, true);

			// slice(1, end) to exlude expander and last item
			// because last item is always expanded...
			const end = this.itemElements.length - 1;
			this.itemElements.slice(1, end).forEach((item) => {
				addClass(item, this.options.hiddenClassName);
				this._updateAria(item, false);
			});
		}

		this._checkWidth();
	}

	private _updateAria(item: HTMLElement, expanded: boolean): void {
		item.setAttribute('aria-expanded', expanded.toString());
	}
}

export function breadcrumb(
	element: HTMLElement,
	options?: BreadcrumbOptions
): Breadcrumb {
	return new Breadcrumb(element, options);
}
